import * as React from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import { ClinicianCredential, IClinician } from "intake_shared/src/types";
import { Button } from "../../bits/Button/Button";
import ChevronDownIcon from "../../icons/ChevronDown";
import { useHistory, useLocation } from "react-router-dom";
import { AclContext } from "../../../../contexts/AclContext";
import Table from "../../bits/Table/Table";
import styles from "./Supervisees.module.scss";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import { Badge } from "../../bits/Badge/Badge";
import { AppRoute } from "../../../../navigation/routes";

interface SuperviseesProps {
    allSupervisees: IClinician[];
}

export default function Supervisees({ allSupervisees }: SuperviseesProps) {
    const history = useHistory();
    const browserLocation = useLocation();

    const [actionRow, setActionRow] = React.useState<IClinician | null>(null);
    const [supervisorEmail] = React.useState(
        (
            (browserLocation.state || {}) as {
                supervisorEmail: string;
            }
        ).supervisorEmail
    );
    const aclContext = React.useContext(AclContext);

    const delegateEmail = supervisorEmail || aclContext?.authUser.user?.email;

    const columns = [
        {
            Header: "Clinician's Name",
            accessor: "fullname",
        },
        {
            Header: "Clinician's Email",
            id: "clinicians_email",
            accessor: (row: IClinician) => (
                <a href={`mailto:${row.email}`}>{row.email}</a>
            ),
        },
        {
            Header: "Locations",
            id: "locations",
            accessor: (row: IClinician) =>
                row.locations.length ? (
                    <div className={styles.locations}>
                        {row.locations.map((location) => (
                            <Badge tone="info" content={location} />
                        ))}
                    </div>
                ) : (
                    "--"
                ),
        },
        {
            Header: "Credential",
            id: "credential",
            accessor: (row: IClinician) =>
                row.credential ? (
                    <Badge
                        content={row.credential}
                        textColor={
                            [
                                ClinicianCredential.MHCLP,
                                ClinicianCredential.LMSW,
                            ].includes(row.credential)
                                ? "#711920"
                                : "#205773"
                        }
                        bgColor={
                            [
                                ClinicianCredential.MHCLP,
                                ClinicianCredential.LMSW,
                            ].includes(row.credential)
                                ? "#FEC1C6"
                                : "#C6ECFF"
                        }
                    />
                ) : (
                    "--"
                ),
        },
        {
            Header: "Contact Info. (Personal)",
            accessor: (row: IClinician) => (
                <span>{row.contact_info_personal || "--"}</span>
            ),
        },
        {
            Header: "Appointments",
            id: "confirmed_appts",
            accessor: (row: IClinician) => row.confirmed.length,
        },
        {
            Header: "Paused",
            id: "paused_appts",
            accessor: (row: IClinician) =>
                row.confirmed.filter((c) => c.is_paused).length,
        },
        {
            Header: "",
            id: "actions",
            accessor: (row: IClinician) => (
                <OverlayTrigger
                    trigger="click"
                    placement="bottom-end"
                    flip
                    rootClose
                    show={actionRow === row}
                    onToggle={(show) => setActionRow(show ? row : null)}
                    overlay={
                        <Popover
                            id="supervisee-row-actions"
                            className={styles.actionsPopover}
                            aria-label="Row actions"
                        >
                            <Popover.Content
                                className={styles.actionsDropdownContent}
                                onClick={() => setActionRow(null)}
                            >
                                <Button
                                    variant="normal"
                                    type="button"
                                    onClick={() => {
                                        history.push(
                                            `/v2${AppRoute.MANAGE_SUPERVISEE}?as=${delegateEmail}`,
                                            {
                                                clinicianEmail: row.email,
                                            }
                                        );
                                    }}
                                >
                                    Manage Caseload
                                </Button>
                            </Popover.Content>
                        </Popover>
                    }
                >
                    <Button
                        aria-expanded={actionRow === row}
                        onClick={(e) => e.stopPropagation()}
                        iconRight={<ChevronDownIcon stroke="var(--color-white)" />}
                    >
                        Actions
                    </Button>
                </OverlayTrigger>
            ),
        },
    ];

    const superviseeListHeader = (
        <div className={styles.superviseesCount}>
            {allSupervisees?.length} Supervisees for{" "}
            {supervisorEmail || aclContext?.authUser.user?.email}
        </div>
    );

    return (
        <div className="v2-page">
            {allSupervisees?.length ? (
                <>
                    {superviseeListHeader}
                    <Table
                        stickyLastColumn
                        columns={columns}
                        data={allSupervisees}
                        allowSelect={false}
                    />
                </>
            ) : (
                <>
                    {superviseeListHeader}
                    <EmptyListState emptyMessage="No supervisees found" />
                </>
            )}
        </div>
    );
}
